import { Loader2, CheckCircle2, XCircle, Ban, Undo2, RefreshCw, AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n";

const ACTIVE = new Set(["queued", "validating", "processing", "cancelling", "rolling_back"]);
const DONE = new Set(["completed", "partially_failed", "failed", "cancelled", "rolled_back"]);

// Compact live progress for a bulk job. Restores from persisted job state, so it
// keeps showing the right status after a refresh or navigation.
export default function BulkJobProgress({ job, onCancel, onRetry, onRollback, onDismiss }) {
  const { t } = useTranslation();
  if (!job) return null;

  const pct = job.totalCount ? Math.round((job.processedCount / job.totalCount) * 100) : 0;
  const remaining = Math.max(0, job.totalCount - job.processedCount);
  const active = ACTIVE.has(job.status);
  const rolledBack = job.status === "rolled_back";

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`bulk-job-${job.jobId}`}>
      <div className="px-4 py-3">
        <div className="flex items-center gap-2">
          {active ? <Loader2 className="h-4 w-4 animate-spin text-slate-600" /> :
            job.status === "completed" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> :
            job.status === "partially_failed" ? <AlertTriangle className="h-4 w-4 text-amber-600" /> :
            job.status === "cancelled" ? <Ban className="h-4 w-4 text-slate-500" /> :
            rolledBack ? <Undo2 className="h-4 w-4 text-slate-500" /> :
            <XCircle className="h-4 w-4 text-rose-600" />}
          <p className="text-sm font-semibold text-slate-900 truncate flex-1" data-testid="bulk-job-title">{job.title}</p>
          <span className="text-[10px] uppercase tracking-wider text-slate-500" data-testid="bulk-job-status">{t(`bulk.status.${job.status}`)}</span>
        </div>

        <p className="text-sm text-slate-700 mt-1" data-testid="bulk-job-progress-text">
          {t("bulk.progressLine", { done: job.processedCount, total: job.totalCount })}
        </p>

        <div className="mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-slate-900 transition-all" style={{ width: `${pct}%` }} data-testid="bulk-job-bar" />
        </div>

        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
          <span data-testid="bulk-job-success">{t("bulk.success", { count: job.successCount })}</span>
          <span data-testid="bulk-job-failed">{t("bulk.failed", { count: job.failedCount })}</span>
          {job.cancelledCount ? <span data-testid="bulk-job-cancelled">{t("bulk.cancelled", { count: job.cancelledCount })}</span> : null}
          <span data-testid="bulk-job-remaining">{t("bulk.remaining", { count: remaining })}</span>
          <span>{pct}%</span>
        </div>
      </div>

      <div className="px-4 py-2.5 flex flex-wrap items-center gap-2 bg-slate-50/60 border-t border-border">
        {active ? (
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => onCancel?.(job)} data-testid="bulk-job-cancel">
            <Ban className="h-3.5 w-3.5 mr-1" /> {t("bulk.cancelJob")}
          </Button>
        ) : null}
        {job.status === "partially_failed" && job.failedCount > 0 ? (
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => onRetry?.(job)} data-testid="bulk-job-retry">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> {t("bulk.retryFailed", { count: job.failedCount })}
          </Button>
        ) : null}
        {(job.status === "completed" || job.status === "partially_failed") ? (
          <Button size="sm" variant="outline" className="rounded-full text-rose-600" onClick={() => onRollback?.(job)} data-testid="bulk-job-rollback">
            <Undo2 className="h-3.5 w-3.5 mr-1" /> {t("bulk.undoAll")}
          </Button>
        ) : null}
        <span className="flex-1" />
        {DONE.has(job.status) ? (
          <Button size="sm" variant="ghost" className="rounded-full text-slate-500" onClick={() => onDismiss?.(job)} data-testid="bulk-job-dismiss">
            {t("bulk.dismiss")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
